import React, { useState, useEffect, useRef } from 'react'
import { priceFormat, showError, showSuccess, processAxiosError, showValidationOrAxiosError } from '../utils/helper'

const LoyaltyModal = ({ show, onClose, onApply, cartTotal, currentLoyaltyData }) => {
    const [view, setView] = useState('search') // search | member | register | assign-card
    const [searchQuery, setSearchQuery] = useState('')
    const [searchResults, setSearchResults] = useState([])
    const [searching, setSearching] = useState(false)
    const [member, setMember] = useState(null)
    const [loading, setLoading] = useState(false)

    // Calculate state
    const [calcData, setCalcData] = useState(null)
    const [pointsToSpend, setPointsToSpend] = useState(0)
    const [couponCode, setCouponCode] = useState('')
    const [couponResult, setCouponResult] = useState(null)
    const [validatingCoupon, setValidatingCoupon] = useState(false)
    const [vipActive, setVipActive] = useState(false)

    // Register state
    const [regForm, setRegForm] = useState({ name: '', surname: '', email: '', phone: '', address: '', city: '', zip: '', dob: '', gdpr_consent: false })
    const [registering, setRegistering] = useState(false)

    // Card assignment state
    const [assignCardNumber, setAssignCardNumber] = useState('')
    const [assigningCard, setAssigningCard] = useState(false)

    const searchInputRef = useRef(null)
    const cardInputRef = useRef(null)
    const searchTimeoutRef = useRef(null)

    // VIP discount calculation
    const vipPercent = (member?.has_vip && member?.vip_discount?.discount_percent) ? member.vip_discount.discount_percent : 0
    // Svi iznosi dolaze iz serverske kotacije (LoyaltyService::quote()) — modal ih ne racuna,
    // pa je ono sto se ovdje prikaze isto ono sto ce se naplatiti i zavrsiti na racunu.
    const quote = calcData?.quote || null
    const vipAmount = parseFloat(quote?.vip_discount) || 0

    useEffect(() => {
        if (!show) return

        if (currentLoyaltyData) {
            // Reopen with existing member — restore state
            setView('member')
            setSearchQuery('')
            setSearchResults([])
            setPointsToSpend(currentLoyaltyData.pointsToSpend || 0)
            setCouponCode(currentLoyaltyData.couponCode || '')
            setCouponResult(currentLoyaltyData.couponCode ? { valid: true } : null)
            setVipActive(currentLoyaltyData.vipActive || false)
            selectMember(currentLoyaltyData.memberId, currentLoyaltyData.pointsToSpend || 0, currentLoyaltyData.couponCode || '', currentLoyaltyData.vipActive || false)
        } else {
            // Fresh open
            setView('search')
            setSearchQuery('')
            setSearchResults([])
            setMember(null)
            setCalcData(null)
            setPointsToSpend(0)
            setCouponCode('')
            setCouponResult(null)
            setVipActive(false)
            setAssignCardNumber('')
            setRegForm({ name: '', surname: '', email: '', phone: '', address: '', city: '', zip: '', dob: '', gdpr_consent: false })
            setTimeout(() => searchInputRef.current?.focus(), 100)
        }
    }, [show])

    // Barcode scanner listener for search view
    useEffect(() => {
        if (!show || (view !== 'search' && view !== 'assign-card')) return

        let buffer = ''
        let lastKeyTime = 0

        function handleKeyDown(e) {
            const now = Date.now()

            if (e.key === 'Enter' && buffer.length >= 4) {
                e.preventDefault()
                const scanned = buffer
                buffer = ''

                if (view === 'assign-card') {
                    setAssignCardNumber(scanned)
                    return
                }

                setSearchQuery(scanned)
                doSearch(scanned)
                return
            }

            // Only accumulate fast keypresses (barcode scanner)
            if (now - lastKeyTime > 50 && buffer.length > 0) {
                buffer = ''
            }

            if (e.key.length === 1) {
                buffer += e.key
                lastKeyTime = now
            }
        }

        document.addEventListener('keydown', handleKeyDown)
        return () => document.removeEventListener('keydown', handleKeyDown)
    }, [show, view])

    // Auto-select when barcode scan returns exactly 1 result
    const prevResultsLenRef = useRef(0)
    useEffect(() => {
        if (searchResults.length === 1 && prevResultsLenRef.current === 0 && !searching) {
            selectMember(searchResults[0].id)
        }
        prevResultsLenRef.current = searchResults.length
    }, [searchResults, searching])

    // Debounced search
    useEffect(() => {
        if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current)
        if (searchQuery.length < 2) {
            setSearchResults([])
            return
        }
        searchTimeoutRef.current = setTimeout(() => {
            doSearch(searchQuery)
        }, 300)
        return () => {
            if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current)
        }
    }, [searchQuery])

    const doSearch = async (q) => {
        setSearching(true)
        try {
            const { data } = await window.axios.get('/loyalty/search', { params: { q } })
            setSearchResults(data.data || [])
        } catch (e) {
            console.error('Loyalty search error:', e?.response?.status, e?.response?.data, e?.message)
            processAxiosError(e)
        } finally {
            setSearching(false)
        }
    }

    const selectMember = async (id, restorePoints = 0, restoreCoupon = '', restoreVip = false) => {
        setLoading(true)
        try {
            const { data } = await window.axios.get(`/loyalty/members/${id}`)
            setMember(data.data)
            setView('member')
            // restoreVip se prosljeduje izricito: setVipActive iz pozivatelja jos nije slegao,
            // pa bi default iz closurea povukao staru vrijednost i kotacija bi bila bez VIP-a.
            await calculate(data.data.id, restorePoints, restoreCoupon, restoreVip)
        } catch (e) {
            processAxiosError(e)
        } finally {
            setLoading(false)
        }
    }

    const calculate = async (memberId, pts, coupon, vip = vipActive) => {
        try {
            const payload = {
                member_id: memberId || member?.id,
                total: parseFloat(cartTotal) || 0,
                points_to_spend: pts || 0,
                apply_vip: !!vip,
            }
            if (coupon) payload.coupon_code = coupon
            const { data } = await window.axios.post('/loyalty/calculate', payload)
            setCalcData(data.data)

            // Clamp pointsToSpend to actual max from API
            const maxPts = data.data?.max_spendable?.max_points || 0
            if (pts > maxPts) {
                setPointsToSpend(maxPts)
            }
        } catch (e) {
            processAxiosError(e)
        }
    }

    const handlePointsChange = (value) => {
        const pts = parseFloat(value) || 0
        const max = calcData?.max_spendable?.max_points || 0
        const clamped = Math.min(Math.max(0, pts), max)
        setPointsToSpend(clamped)
        calculate(member?.id, clamped, couponResult?.valid ? couponCode : '')
    }

    const handleSpendMax = () => {
        const max = calcData?.max_spendable?.max_points || 0
        setPointsToSpend(max)
        calculate(member?.id, max, couponResult?.valid ? couponCode : '')
    }

    const handleClearPoints = () => {
        setPointsToSpend(0)
        calculate(member?.id, 0, couponResult?.valid ? couponCode : '')
    }

    const handleValidateCoupon = async () => {
        if (!couponCode.trim()) return
        setValidatingCoupon(true)
        try {
            const { data } = await window.axios.post('/loyalty/validate-coupon', {
                code: couponCode,
                member_id: member?.id,
                order_total: parseFloat(cartTotal) || 0,
            })
            setCouponResult(data.data)
            if (data.data.valid) {
                calculate(member?.id, pointsToSpend, couponCode)
            }
        } catch (e) {
            processAxiosError(e)
        } finally {
            setValidatingCoupon(false)
        }
    }

    const handleClearCoupon = () => {
        setCouponCode('')
        setCouponResult(null)
        calculate(member?.id, pointsToSpend, '')
    }

    const getTotalDiscount = () => {
        return parseFloat(quote?.total_discount) || 0
    }

    const handleApply = () => {
        // Prosljeduju se samo ULAZI. Iznosi se namjerno ne nose sa sobom — zamrznuti iznos
        // zastari cim se kosarica promijeni, a mjerodavan je onaj koji server vrati.
        onApply({
            memberId: member.id,
            memberName: trimStr(`${member.user?.name || ''} ${member.user?.surname || ''}`),
            cardNumber: member.card_number,
            pointsBalance: member.points_balance,
            pointsToSpend: pointsToSpend,
            couponCode: couponResult?.valid ? couponCode : null,
            vipActive: vipActive,
            earnablePoints: calcData?.earnable_points || 0,
            earnPercentage: calcData?.earn_percentage || 10,
        })
        onClose()
    }

    const handleRemoveMember = () => {
        onApply(null)
        onClose()
    }

    const handleChangeMember = () => {
        setMember(null)
        setCalcData(null)
        setPointsToSpend(0)
        setCouponCode('')
        setCouponResult(null)
        setVipActive(false)
        setView('search')
        setSearchQuery('')
        setSearchResults([])
        setTimeout(() => searchInputRef.current?.focus(), 100)
    }

    const handleRegister = async () => {
        if (!regForm.name || !regForm.surname) {
            showError('Ime i prezime su obavezni.')
            return
        }
        if (!regForm.email) {
            showError('Email je obavezan.')
            return
        }
        if (!regForm.gdpr_consent) {
            showError('GDPR suglasnost je obavezna.')
            return
        }

        setRegistering(true)
        try {
            const { data } = await window.axios.post('/loyalty/register', regForm)
            showSuccess('Clan uspjesno registriran!')

            // If member has no card, go to card assignment view
            if (data.data.has_card === false) {
                setMember(null) // will be loaded in assign-card view after assignment
                // Store member id temporarily
                setAssignCardNumber('')
                setView('assign-card')
                // Save member id for assignment
                window.__loyaltyNewMemberId = data.data.id
                setTimeout(() => cardInputRef.current?.focus(), 100)
            } else {
                await selectMember(data.data.id)
            }
        } catch (e) {
            processAxiosError(e)
        } finally {
            setRegistering(false)
        }
    }

    const handleAssignCard = async () => {
        if (!assignCardNumber.trim()) {
            showError('Skenirajte ili upišite broj kartice.')
            return
        }

        const memberId = member?.id || window.__loyaltyNewMemberId
        if (!memberId) {
            showError('Clan nije odabran.')
            return
        }

        setAssigningCard(true)
        try {
            const { data } = await window.axios.post('/loyalty/assign-card', {
                member_id: memberId,
                card_number: assignCardNumber.trim(),
            })
            showSuccess('Kartica dodijeljena: ' + data.data.card_number)
            window.__loyaltyNewMemberId = null
            setAssignCardNumber('')
            await selectMember(data.data.id)
        } catch (e) {
            showValidationOrAxiosError(e)
            setAssignCardNumber('')
        } finally {
            setAssigningCard(false)
        }
    }

    const handleSkipCardAssignment = async () => {
        const memberId = member?.id || window.__loyaltyNewMemberId
        window.__loyaltyNewMemberId = null
        if (memberId) {
            await selectMember(memberId)
        }
    }

    function trimStr(s) { return s.replace(/\s+/g, ' ').trim() }

    if (!show) return null

    return (
        <div className="fixed w-full h-screen left-0 top-0 z-50 flex flex-wrap justify-center content-center p-24">
            <div onClick={onClose} className="fixed glass w-full h-screen left-0 top-0 z-0 opacity-100"></div>
            <div className="w-[480px] max-h-[90vh] rounded-sm bg-white shadow-xl overflow-hidden z-10 flex flex-col">
                {/* Header */}
                <div className="bg-primary px-4 py-3 flex items-center justify-between">
                    <h2 className="text-lg font-bold text-black">Klub Vjernosti</h2>
                    <button onClick={onClose} className="text-black hover:text-gray-700">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <div className="flex-1 overflow-auto p-4">
                    {/* SEARCH VIEW */}
                    {view === 'search' && (
                        <div>
                            <div className="flex gap-2 mb-3">
                                <input
                                    ref={searchInputRef}
                                    type="text"
                                    value={searchQuery}
                                    onChange={e => setSearchQuery(e.target.value)}
                                    placeholder="Kartica, telefon, email, ime..."
                                    className="flex-1 px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400"
                                />
                                <button
                                    onClick={() => setView('register')}
                                    className="px-3 py-2 text-sm bg-green-500 text-white rounded-lg hover:bg-green-600"
                                >
                                    + Novi
                                </button>
                            </div>

                            {searching && (
                                <div className="text-center py-4 text-gray-500 text-sm">Pretraga...</div>
                            )}

                            {!searching && searchResults.length === 0 && searchQuery.length >= 2 && (
                                <div className="text-center py-4 text-gray-400 text-sm">Nema rezultata</div>
                            )}

                            <div className="space-y-2">
                                {searchResults.map(m => (
                                    <button
                                        key={m.id}
                                        onClick={() => selectMember(m.id)}
                                        className="w-full text-left p-3 border rounded-lg hover:bg-yellow-50 hover:border-yellow-400 transition"
                                    >
                                        <div className="flex justify-between items-start">
                                            <div>
                                                <div className="font-semibold text-sm">
                                                    {m.user?.name} {m.user?.surname}
                                                </div>
                                                <div className="text-xs text-gray-500">
                                                    {m.card_number || <span className="text-orange-500">Bez kartice</span>}
                                                    {m.user?.phone && ` | ${m.user.phone}`}
                                                    {m.user?.email && ` | ${m.user.email}`}
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <div className="text-sm font-semibold text-yellow-600">
                                                    {m.points_balance} bod.
                                                </div>
                                                {m.has_vip && (
                                                    <span className="text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded">VIP</span>
                                                )}
                                                {m.has_card === false && (
                                                    <span className="text-xs bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded">Bez kartice</span>
                                                )}
                                            </div>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* MEMBER VIEW */}
                    {view === 'member' && member && (
                        <div>
                            {/* Member info card */}
                            <div className="bg-gray-50 rounded-lg p-3 mb-4">
                                <div className="flex justify-between items-start">
                                    <div>
                                        <div className="font-bold text-base">
                                            {member.user?.name} {member.user?.surname}
                                        </div>
                                        <div className="text-xs text-gray-500 mt-0.5">
                                            {member.card_number || <span className="text-orange-500 font-semibold">Bez kartice</span>}
                                            {member.user?.phone && ` | ${member.user.phone}`}
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-lg font-bold text-yellow-600">
                                            {member.points_balance} bod.
                                        </div>
                                        {member.has_vip && member.vip_discount && (
                                            <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">
                                                VIP -{member.vip_discount.discount_percent}%
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div className="flex gap-2 mt-2">
                                    <button
                                        onClick={handleChangeMember}
                                        className="text-xs text-blue-600 hover:text-blue-800 hover:underline"
                                    >
                                        Promijeni clana
                                    </button>
                                    <span className="text-xs text-gray-300">|</span>
                                    <button
                                        onClick={handleRemoveMember}
                                        className="text-xs text-red-500 hover:text-red-700 hover:underline"
                                    >
                                        Ukloni clana
                                    </button>
                                    {member.has_card === false && (
                                        <>
                                            <span className="text-xs text-gray-300">|</span>
                                            <button
                                                onClick={() => { setAssignCardNumber(''); setView('assign-card'); setTimeout(() => cardInputRef.current?.focus(), 100) }}
                                                className="text-xs text-green-600 hover:text-green-800 hover:underline font-semibold"
                                            >
                                                Dodijeli karticu
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>

                            {/* Card assignment prompt for members without card */}
                            {member.has_card === false && (
                                <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-3">
                                    <div className="flex items-center gap-2 text-sm text-orange-700">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                        </svg>
                                        <span>Clan nema dodijeljenu karticu.</span>
                                    </div>
                                </div>
                            )}

                            {calcData && (
                                <>
                                    {/* Earnable points */}
                                    <div className="flex justify-between items-center mb-3 px-1">
                                        <span className="text-sm text-gray-600">Zarada za ovu kupnju:</span>
                                        <span className="text-sm font-semibold text-green-600">
                                            +{calcData.earnable_points} bodova
                                        </span>
                                    </div>

                                    {/* VIP Discount */}
                                    {vipPercent > 0 && (
                                        <div className={`border rounded-lg p-3 mb-3 ${vipActive ? 'border-purple-400 bg-purple-50' : ''}`}>
                                            <div className="flex justify-between items-center">
                                                <div>
                                                    <span className="text-sm font-semibold">VIP popust</span>
                                                    <span className="ml-2 text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded">
                                                        -{vipPercent}%
                                                    </span>
                                                </div>
                                                <button
                                                    onClick={() => {
                                                        // Prekidac mijenja ULAZ, pa se kotacija mora povuci ponovno.
                                                        const next = !vipActive
                                                        setVipActive(next)
                                                        calculate(member?.id, pointsToSpend, couponResult?.valid ? couponCode : '', next)
                                                    }}
                                                    className={`px-4 py-1.5 text-sm rounded-lg font-semibold ${
                                                        vipActive
                                                            ? 'bg-red-100 text-red-700 hover:bg-red-200'
                                                            : 'bg-purple-500 text-white hover:bg-purple-600'
                                                    }`}
                                                >
                                                    {vipActive ? 'Makni' : 'Primijeni'}
                                                </button>
                                            </div>
                                            {vipActive && (
                                                <div className="mt-2 text-sm text-red-600 font-semibold">
                                                    Popust: -{priceFormat(vipAmount)}
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Spend points */}
                                    {calcData.max_spendable?.max_points > 0 && (
                                        <div className="border rounded-lg p-3 mb-3">
                                            <div className="flex justify-between items-center mb-2">
                                                <span className="text-sm font-semibold">Iskoristi bodove</span>
                                                <span className="text-xs text-gray-500">
                                                    Max: {calcData.max_spendable.max_points} bod. ({priceFormat(calcData.max_spendable.euro_value)})
                                                </span>
                                            </div>
                                            <div className="flex gap-2">
                                                <input
                                                    type="number"
                                                    value={pointsToSpend || ''}
                                                    onChange={e => handlePointsChange(e.target.value)}
                                                    min={0}
                                                    max={calcData.max_spendable.max_points}
                                                    placeholder="0"
                                                    className="flex-1 px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400"
                                                />
                                                {pointsToSpend > 0 ? (
                                                    <button
                                                        onClick={handleClearPoints}
                                                        className="px-3 py-2 text-sm bg-red-100 text-red-700 rounded-lg hover:bg-red-200 font-semibold"
                                                    >
                                                        Makni
                                                    </button>
                                                ) : (
                                                    <button
                                                        onClick={handleSpendMax}
                                                        className="px-3 py-2 text-sm bg-yellow-100 text-yellow-800 rounded-lg hover:bg-yellow-200 font-semibold"
                                                    >
                                                        MAX
                                                    </button>
                                                )}
                                            </div>
                                            {calcData.points_spend && calcData.points_spend.euro_value > 0 && (
                                                <div className="mt-2 text-sm text-red-600 font-semibold">
                                                    Popust: -{priceFormat(calcData.points_spend.euro_value)}
                                                </div>
                                            )}
                                            {calcData.points_spend?.error && (
                                                <div className="mt-2 text-xs text-red-500">{calcData.points_spend.error}</div>
                                            )}
                                        </div>
                                    )}

                                    {/* Coupon */}
                                    <div className="border rounded-lg p-3 mb-3">
                                        <div className="text-sm font-semibold mb-2">Kupon</div>
                                        <div className="flex gap-2">
                                            <input
                                                type="text"
                                                value={couponCode}
                                                onChange={e => { setCouponCode(e.target.value.toUpperCase()); setCouponResult(null) }}
                                                placeholder="Kod kupona..."
                                                className="flex-1 px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400"
                                            />
                                            {couponResult?.valid ? (
                                                <button onClick={handleClearCoupon} className="px-3 py-2 text-sm bg-red-100 text-red-700 rounded-lg hover:bg-red-200">
                                                    Ukloni
                                                </button>
                                            ) : (
                                                <button
                                                    onClick={handleValidateCoupon}
                                                    disabled={validatingCoupon || !couponCode.trim()}
                                                    className="px-3 py-2 text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50"
                                                >
                                                    {validatingCoupon ? '...' : 'Provjeri'}
                                                </button>
                                            )}
                                        </div>
                                        {couponResult && !couponResult.valid && (
                                            <div className="mt-2 text-xs text-red-500">{couponResult.error}</div>
                                        )}
                                        {couponResult?.valid && (
                                            <div className="mt-2 text-sm text-green-600 font-semibold">
                                                Kupon aktivan: -{priceFormat(calcData?.coupon?.discount_amount || 0)}
                                            </div>
                                        )}
                                    </div>

                                    {/* Total discount summary */}
                                    {getTotalDiscount() > 0 && (
                                        <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-3 mb-3">
                                            {vipActive && vipAmount > 0 && (
                                                <div className="flex justify-between text-sm mb-1">
                                                    <span className="text-purple-700">VIP -{vipPercent}%</span>
                                                    <span className="text-red-600">-{priceFormat(vipAmount)}</span>
                                                </div>
                                            )}
                                            {calcData.points_spend?.euro_value > 0 && (
                                                <div className="flex justify-between text-sm mb-1">
                                                    <span className="text-gray-600">Bodovi ({pointsToSpend} bod.)</span>
                                                    <span className="text-red-600">-{priceFormat(calcData.points_spend.euro_value)}</span>
                                                </div>
                                            )}
                                            {calcData.coupon?.discount_amount > 0 && (
                                                <div className="flex justify-between text-sm mb-1">
                                                    <span className="text-gray-600">Kupon ({couponCode})</span>
                                                    <span className="text-red-600">-{priceFormat(calcData.coupon.discount_amount)}</span>
                                                </div>
                                            )}
                                            <div className="flex justify-between text-sm font-bold border-t border-yellow-300 pt-1 mt-1">
                                                <span>Loyalty popust ukupno:</span>
                                                <span className="text-red-600">
                                                    -{priceFormat(getTotalDiscount())}
                                                </span>
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}

                            {loading && <div className="text-center py-4 text-gray-500 text-sm">Ucitavanje...</div>}
                        </div>
                    )}

                    {/* ASSIGN CARD VIEW */}
                    {view === 'assign-card' && (
                        <div>
                            <button onClick={() => member ? setView('member') : handleSkipCardAssignment()} className="text-sm text-gray-500 hover:text-gray-700 mb-3 flex items-center">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                </svg>
                                Natrag
                            </button>

                            <div className="text-center mb-4">
                                <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-green-50 flex items-center justify-center">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                                    </svg>
                                </div>
                                <h3 className="font-semibold text-base mb-1">Dodijeli karticu</h3>
                                <p className="text-sm text-gray-500">Skenirajte barkod kartice ili upišite broj</p>
                            </div>

                            <input
                                ref={cardInputRef}
                                type="text"
                                value={assignCardNumber}
                                onChange={e => setAssignCardNumber(e.target.value.toUpperCase())}
                                onKeyDown={e => { if (e.key === 'Enter') handleAssignCard() }}
                                placeholder="Skeniraj ili upiši broj kartice..."
                                className="w-full px-4 py-3 text-center text-lg font-mono font-bold tracking-widest border-2 border-green-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-400 mb-4"
                                autoFocus
                            />

                            <button
                                onClick={handleAssignCard}
                                disabled={assigningCard || !assignCardNumber.trim()}
                                className="w-full py-2.5 text-sm font-bold bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 mb-2"
                            >
                                {assigningCard ? 'Dodjela...' : 'Dodijeli karticu'}
                            </button>

                            <button
                                onClick={handleSkipCardAssignment}
                                className="w-full py-2 text-xs text-gray-400 hover:text-gray-600"
                            >
                                Preskoči - nastavi bez kartice
                            </button>
                        </div>
                    )}

                    {/* REGISTER VIEW */}
                    {view === 'register' && (
                        <div>
                            <button onClick={() => setView('search')} className="text-sm text-gray-500 hover:text-gray-700 mb-3 flex items-center">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                </svg>
                                Natrag na pretragu
                            </button>

                            <h3 className="font-semibold text-base mb-3">Novi Clan</h3>

                            <div className="space-y-3">
                                <input
                                    type="text"
                                    value={regForm.name}
                                    onChange={e => setRegForm({ ...regForm, name: e.target.value })}
                                    placeholder="Ime *"
                                    className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400"
                                />
                                <input
                                    type="text"
                                    value={regForm.surname}
                                    onChange={e => setRegForm({ ...regForm, surname: e.target.value })}
                                    placeholder="Prezime *"
                                    className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400"
                                />
                                <input
                                    type="email"
                                    value={regForm.email}
                                    onChange={e => setRegForm({ ...regForm, email: e.target.value })}
                                    placeholder="Email *"
                                    className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400"
                                />
                                <input
                                    type="tel"
                                    value={regForm.phone}
                                    onChange={e => setRegForm({ ...regForm, phone: e.target.value })}
                                    placeholder="Telefon"
                                    className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400"
                                />
                                <input
                                    type="text"
                                    value={regForm.address}
                                    onChange={e => setRegForm({ ...regForm, address: e.target.value })}
                                    placeholder="Adresa"
                                    className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400"
                                />
                                <div className="flex gap-2">
                                    <input
                                        type="text"
                                        value={regForm.city}
                                        onChange={e => setRegForm({ ...regForm, city: e.target.value })}
                                        placeholder="Grad"
                                        className="flex-1 px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400"
                                    />
                                    <input
                                        type="text"
                                        value={regForm.zip}
                                        onChange={e => setRegForm({ ...regForm, zip: e.target.value })}
                                        placeholder="Poštanski broj"
                                        className="w-1/3 px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400"
                                    />
                                </div>
                                <input
                                    type="date"
                                    value={regForm.dob}
                                    onChange={e => setRegForm({ ...regForm, dob: e.target.value })}
                                    placeholder="Datum rođenja"
                                    className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400"
                                />
                                <label className="flex items-start gap-2 text-sm">
                                    <input
                                        type="checkbox"
                                        checked={regForm.gdpr_consent}
                                        onChange={e => setRegForm({ ...regForm, gdpr_consent: e.target.checked })}
                                        className="mt-0.5"
                                    />
                                    <span className="text-gray-600">
                                        Suglasan/na sam s prikupljanjem i obradom osobnih podataka u svrhu programa vjernosti (GDPR) *
                                    </span>
                                </label>
                            </div>

                            <button
                                onClick={handleRegister}
                                disabled={registering}
                                className="w-full mt-4 py-2.5 text-sm font-bold bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50"
                            >
                                {registering ? 'Registracija...' : 'Registriraj Clana'}
                            </button>
                        </div>
                    )}
                </div>

                {/* Footer buttons */}
                {view === 'member' && member && (
                    <div className="border-t p-4">
                        <button
                            onClick={handleApply}
                            className="w-full py-2.5 text-sm font-bold bg-primary text-black rounded-lg hover:bg-yellow-500"
                        >
                            Primijeni
                        </button>
                    </div>
                )}
            </div>
        </div>
    )
}

export default LoyaltyModal
